import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { Icon } from "@/components/icons/Icon";
import { Sidebar } from "@/components/sidebar";
import { NumberInput } from "@/components/sidebar/NumberInput";
import { ActiveTooltip } from "@/components/tooltip/Tooltip";

import { GraphZoom } from "./GraphZoom";

import type { GraphNodeAttrs } from "@/components/tiptap/Graph";
import type { GraphNodeModel } from "@/models/nodes/GraphNodeModel";

type Props = {
  node: GraphNodeModel;
  graphAttrs: GraphNodeAttrs;
};

export const GraphLayout = observer(function GraphLayout({ node, graphAttrs }: Props) {
  const t = useTranslations("sidebar.activeNode");
  const { doc } = node;
  const { tiptap } = doc.editor;

  if (!tiptap) return null;

  const onLayoutChange = (attrs: Partial<GraphNodeAttrs>) => {
    tiptap.commands.updateAttributes("graph", attrs);
  };

  return (
    <>
      <Sidebar.SectionTitle className="text-xxxs text-gray-500 pt-0">
        {t("graph.title.layout")}
      </Sidebar.SectionTitle>
      <div className="grid grid-cols-3 gap-4">
        <ActiveTooltip title={t("fields.tooltips.dimensions.width")} focusDisabled>
          <NumberInput
            value={graphAttrs.width}
            min={20}
            max={window.innerWidth ?? 1000}
            aria-label={t("graph.actions.graphWidth")}
            icon={<Icon name="Width" />}
            renderButtons={false}
            onSubmit={(value) => onLayoutChange({ width: value })}
          />
        </ActiveTooltip>
        <ActiveTooltip title={t("fields.tooltips.dimensions.height")} focusDisabled>
          <NumberInput
            value={graphAttrs.height}
            min={20}
            aria-label={t("graph.actions.graphHeight")}
            max={window.innerHeight ?? 1000}
            icon={<Icon name="Height" />}
            renderButtons={false}
            onSubmit={(value) => onLayoutChange({ height: value })}
          />
        </ActiveTooltip>
        <GraphZoom value={graphAttrs.zoom} onChange={(value) => onLayoutChange({ zoom: value })} />
      </div>
    </>
  );
});
